import React, {useState, useEffect, useContext, useCallback} from "react"
import * as Order from "../../dtos/orderResponse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import clsx from "clsx"
import LineItems from "./lineItem/LineItems"
import {OrderActions} from "./orderActions/OrderActions"
import OrderService from "../../services/order/OrderService"
import TenantService from "../../services/tenant/TenantService"
// import FinanceHistory from "./financeHistory/FinanceHistory"
import {PaymentHistory} from "../../services/order/paymentHistory"
import {OrderCredits} from "../../services/order/CreditDetails"
import {TenantResponse} from "../../dtos/tenantResponse"
import {Event, EventResponse} from "../../dtos/eventResponse"
import OrderOverview from "./OrderOverview"
// import CreditHistory from "./creditHistory/CreditHistory"
import PermissionValidator from "../../services/coam/PermissionValidator"
import {TenantsContext} from "../../contexts/TenantsContext"
import {getField} from "../../utils/objectHelper"
import {postActionLog, TabActionTypes} from "../../services/order/TrackOrderActions"
import {getCaseId} from "../../utils/UrlParser"
import {OrderItemFailure} from "../../dtos/orderFailuresResponse"
// import OrderFailures from "./orderFailures/orderFailures"
import EventHistoryV2 from "./eventHistory/EventHistoryV2"
import {makeStyles} from "tss-react/mui"
import {useQueryClient} from "@tanstack/react-query"
import {orderListKeys} from "@commercehub/api-service"
import {
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Collapse,
	IconButton,
	Tab,
	Tabs,
	Typography,
} from "@mui/material"
import {CreditHistory, FinanceHistory, OrderFailures} from "@commercehub/order"

const caseId = getCaseId()
const tenantsvc = new TenantService()
const ordersvc = new OrderService()

interface OrderDetailsProps {
	customerId: string
	order: Order.Order
	expandItems?: boolean
}

const useStyles = makeStyles()((theme) => ({
	card: {
		padding: "0px",
		marginTop: "15px",
	},
	expand: {
		transform: "rotate(0deg)",
		marginLeft: "auto",
		transition: theme.transitions.create("transform", {
			duration: theme.transitions.duration.shortest,
		}),
	},
	expandOpen: {
		transform: "rotate(180deg)",
	},
	title: {
		fontSize: "18px!important",
	},
	label: {
		fontSize: ".75rem",
	},
	cardContent: {
		paddingLeft: "30px",
		paddingRight: "50px",
	},
}))

export default function OrderDetails(props: OrderDetailsProps) {
	const svc = new OrderService()
	const {classes} = useStyles()
	const [order, setOrder] = useState(props.order)
	const [expanded, setExpanded] = useState(props.expandItems)
	const [value, setValue] = React.useState(0)
	const [financeHist, setFinanceHist] = useState<PaymentHistory>()
	const [eventHist, setEventHist] = useState<Event[]>([])
	const [eventResponse, setEventResponse] = useState<EventResponse | null>(null)
	const [paymentFullySuccessful, setPaymenFullySuccessful] = useState(false)
	const {tenants} = useContext(TenantsContext)
	const [orderCredits, setOrderCredits] = useState<OrderCredits | null>(null)
	const [orderItemFailures, setOrderItemFailures] = useState<OrderItemFailure[]>([])
	const isVCSOrder = props.order.orderData.tenantId.startsWith("VCS")
	const [isVCSAgent, setIsVCSAgent] = useState(false)
	const [startTime, setStartTime] = useState(new Date().getTime())
	const queryClient = useQueryClient()
	const getDefaultCulture = () => {
		if (order.orderData && order.orderData.locale && order.orderData.locale !== "") {
			return order.orderData.locale
		}
		if (
			order.orderData &&
			order.orderData.customOrderData &&
			order.orderData.customOrderData.site &&
			order.orderData.customOrderData.site.locale &&
			order.orderData.customOrderData.site.locale !== ""
		) {
			return order.orderData.customOrderData.site.locale
		}
		return ""
	}
	const getDefaultCountryCode = (): string => {
		const defaultCulture = getDefaultCulture()
		if (defaultCulture !== "") {
			const defaultCountryCode =
				defaultCulture.split("-").length > 1 ? defaultCulture.split("-")[1] : ""
			return defaultCountryCode
		}
		return ""
	}
	const [defaultCulture, setDefaultCulture] = useState(getDefaultCulture())
	const [defaultCountryCode, setDefaultCountryCode] = useState(getDefaultCountryCode())
	const logActionToSnowflake = (action: string, handleTime?: number) => {
		postActionLog(
			{
				event: action,
				actionMetaData: {
					orderNumber: props.order.orderNumber,
					handleTime: handleTime,
				},
			},
			caseId
		)
	}

	function handleChange(event: React.ChangeEvent<{}>, newValue: number) {
		let action = ""
		const oldSelectedTab = value
		switch (newValue) {
			case 1:
				action = TabActionTypes.OpenFinanceHistoryTab
				break
			case 2: {
				setStartTime(new Date().getTime())
				action = TabActionTypes.OpenEventHistoryTab
				break
			}
			case 3:
				action = TabActionTypes.OpenCreditHistoryTab
				break
			case 4:
				action = TabActionTypes.OpenOrderFailuresTab
				break
			default:
				break
		}
		setValue(newValue)
		if (oldSelectedTab === 2) {
			action = TabActionTypes.LeaveEventHistoryTab
		}
		if (action !== "") {
			if (action === TabActionTypes.LeaveEventHistoryTab) {
				const elapsedTime = new Date().getTime() - startTime
				const elapsedSeconds = elapsedTime / 1000
				logActionToSnowflake(action, elapsedSeconds)
			} else {
				logActionToSnowflake(action)
			}
		}
	}

	function handleExpandClick() {
		setExpanded(!expanded)
	}

	async function updateOrder() {
		const newOrder = await svc.getOrder(order.orderNumber)
		queryClient.invalidateQueries({
			queryKey: orderListKeys.detail(order.orderNumber),
		})
		if (newOrder) {
			setOrder(newOrder)
			updateFinanceHist()
			updateEventHistory()
		}
	}

	const updateFinanceHist = async () => {
		const finHist = await ordersvc.getFinanceHistory(order.orderNumber)
		setFinanceHist(finHist)
	}

	const updateEventHistory = async () => {
		const eventResponse = await ordersvc.getOrderEventsV2(order.orderNumber, -1, "")
		if (eventResponse) {
			setEventResponse(eventResponse)
		}
		const evHist = getOrderEvents(eventResponse)
		setEventHist(evHist)
	}

	const isPaymentFullySuccessful = useCallback(
		(financeHistory: PaymentHistory) => {
			if (financeHistory) {
				if (
					getField({
						obj: financeHistory,
						key: "totalCaptured.amountInCents",
						defaultValue: 0,
					}) === 0
				) {
					return false
				}
				const successfulPayments = financeHistory.payments.filter(
					(payment) =>
						getField({
							obj: payment,
							key: "paymentStatus.interfaceCode",
							defaultValue: "",
						}).toString() === "Paid"
				)
				if (successfulPayments && successfulPayments.length > 0) {
					let successfullyPaidAmount = 0
					successfulPayments.forEach((payment) => {
						const paymentAmount = getField({
							obj: payment,
							key: "amount.amountInCents",
							defaultValue: 0,
						})
						successfullyPaidAmount += paymentAmount
					})
					const orderTotal = getField({
						obj: order,
						key: "orderData.orderTotal.centAmount",
						defaultValue: 0,
					})
					return successfullyPaidAmount === orderTotal
				}
			}
			return false
		},
		[order]
	)

	useEffect(() => {
		const initFinanceHist = async () => {
			const finHist = await ordersvc.getFinanceHistory(order.orderNumber)
			setFinanceHist(finHist)
			const paymentSuccessul = isPaymentFullySuccessful(finHist)
			setPaymenFullySuccessful(paymentSuccessul)
		}
		initFinanceHist()
	}, [order.orderNumber, isPaymentFullySuccessful])

	useEffect(() => {
		const fetchCreditHistory = async () => {
			const data = await ordersvc.getOrderCredits(order.orderNumber)
			setOrderCredits(data)
		}
		const fetchOrderFailures = async () => {
			const data = await ordersvc.getOrderItemFailures(order.orderNumber)
			setOrderItemFailures(data)
		}
		fetchCreditHistory()
		fetchOrderFailures()
	}, [order.orderNumber, props.order])

	useEffect(() => {
		async function checkPermissions() {
			if (isVCSOrder) {
				const permissionValidator = new PermissionValidator()
				const isVCSAgent = await permissionValidator.hasPermission(
					"care-console",
					"*",
					"manage:vcs_order"
				)
				setIsVCSAgent(isVCSAgent)
			}
		}
		checkPermissions()
	}, [isVCSOrder])
	const getOrderEvents = (eventResponse: EventResponse): Event[] => {
		if (eventResponse && eventResponse.orderEvents && eventResponse.orderEvents.length > 0) {
			const events = eventResponse.orderEvents.map((orderEvent) => {
				return orderEvent.event
			})
			return events
		}
		return []
	}
	useEffect(() => {
		const initEventHist = async () => {
			let i = 0
			let eventsCount = 200
			let events: EventResponse = {
				itemEvents: [],
				orderEvents: [],
			}
			while (eventsCount === 200) {
				const eventResponse = await ordersvc.getOrderEventsV2(order.orderNumber, -1, "", i)
				events.orderEvents.push(...eventResponse.orderEvents)
				eventResponse.itemEvents.forEach((itemEvent) => {
					const filteredEvents = events.itemEvents.filter(
						(item) => item.itemdId === itemEvent.itemdId
					)
					if (filteredEvents.length > 0) {
						filteredEvents[0].events.push(...itemEvent.events)
					} else {
						events.itemEvents.push(itemEvent)
					}
				})
				setEventResponse(events)
				eventsCount = eventResponse.orderEvents.length
				i++
			}
			const evHist = getOrderEvents(events)
			if (
				order.isLegacy &&
				order.orderData.customOrderData &&
				order.orderData.customOrderData.care &&
				order.orderData.customOrderData.care.orderEvents
			) {
				evHist.push(...order.orderData.customOrderData.care.orderEvents)
				evHist.sort(
					(event1, event2) =>
						new Date(event2.dateTime).getTime() - new Date(event1.dateTime).getTime()
				)
			}
			setEventHist(evHist)
		}
		const updateDefaultCulture = async () => {
			let tenant: TenantResponse
			tenant = await tenantsvc.getTenantDetails(order.orderData.tenantId, tenants)
			const defaultCulture = tenant.defaultCulture
			setDefaultCulture(defaultCulture)
			setDefaultCountryCode(tenant.defaultCountryCode)
		}
		if (defaultCulture === "") {
			updateDefaultCulture()
		}
		initEventHist()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		order.orderId,
		setFinanceHist,
		setEventHist,
		order.orderData.tenantId,
		order.orderNumber,
		tenants,
		order.orderData,
		order.isLegacy,
		setEventResponse,
	])

	const isFullyPaidWithMC = () => {
		if (order.orderData.payments && order.orderData.payments[0]) {
			return (
				(order.orderData.payments[0].paymentMethod || "").toLowerCase() === "credits" &&
				order.orderData.payments[0].cents === order.orderData.orderTotal.centAmount &&
				order.orderData.payments[0].currencyCode === order.orderData.orderTotal.currencyCode &&
				order.orderData.payments[0].state === "Paid"
			)
		} else {
			return false
		}
	}

	const isCompanyCardOrder = () => {
		return (
			order.orderData.payments?.some(
				(payment) =>
					payment.paymentMethod === "PayOnBillingAccount" &&
					payment.paymentMethodMetadata?.PaymentTiming === "PrePaid" &&
					payment.state !== "Failed"
			) ?? false
		)
	}

	const isPayOnBillingAccountOrder = () => {
		return (
			order.orderData.payments?.some(
				(payment) =>
					payment.paymentMethod === "PayOnBillingAccount" &&
					payment.paymentMethodMetadata?.PaymentTiming !== "PrePaid" &&
					payment.state !== "Failed"
			) ?? false
		)
	}

	return (
		<>
			<Card className={classes.card}>
				<CardHeader
					classes={{
						title: classes.title,
					}}
					title={
						order.orderNumber +
						(order.isLegacy ? " - Legacy Order, only credits are available." : "")
					}
					subheader={
						order.orderData.orderPlacedAnonymously && (
							<Typography style={{color: "red"}}>Anonymous Order</Typography>
						)
					}
					action={
						<>
							{financeHist ? (
								<OrderActions
									updateOrder={updateOrder}
									customerId={props.customerId}
									refInfo={{
										totalCaptured: financeHist.totalCaptured,
										totalRemaining: financeHist.totalRemaining,
									}}
									order={order}
									finHist={financeHist}
									isFullyPaidWithMC={isFullyPaidWithMC()}
									paymentFullySuccessful={paymentFullySuccessful}
									orderCredits={orderCredits}
									orderIsReleasedToFulfillment={order.orderData.releasedToFulfillment}
									eventHist={eventHist}
									isVCSAgent={isVCSAgent}
									isVCSOrder={isVCSOrder}
									isPayOnBillingAccountOrder={isPayOnBillingAccountOrder()}
									isCompanyCardOrder={isCompanyCardOrder()}
								/>
							) : (
								<OrderActions
									updateOrder={updateOrder}
									customerId={props.customerId}
									refInfo={{
										totalCaptured: {
											amountInCents: order.orderData.orderTotal.centAmount,
											currency: order.orderData.orderTotal.currencyCode,
											fractionDigits: order.orderData.orderTotal.fractionDigits,
										},
										totalRemaining: {
											amountInCents: 0,
											currency: order.orderData.orderTotal.currencyCode,
											fractionDigits: order.orderData.orderTotal.fractionDigits,
										},
									}}
									order={order}
									finHist={financeHist}
									isFullyPaidWithMC={isFullyPaidWithMC()}
									paymentFullySuccessful={paymentFullySuccessful}
									orderCredits={orderCredits}
									orderIsReleasedToFulfillment={order.orderData.releasedToFulfillment}
									eventHist={eventHist}
									isVCSAgent={isVCSAgent}
									isVCSOrder={isVCSOrder}
									isPayOnBillingAccountOrder={isPayOnBillingAccountOrder()}
									isCompanyCardOrder={isCompanyCardOrder()}
								/>
							)}
						</>
					}
				/>
				<OrderOverview
					customerId={props.customerId}
					order={order}
					updateOrder={updateOrder}
					isVCSAgent={isVCSAgent}
					isVCSOrder={isVCSOrder}
					eventResponse={eventResponse}
					isOrderPaid={
						financeHist &&
						financeHist.totalCaptured &&
						financeHist.totalCaptured.amountInCents !== 0
					}
				></OrderOverview>
				<CardActions disableSpacing>
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: expanded,
						})}
						onClick={handleExpandClick}
						aria-expanded={expanded}
						aria-label="Show more"
					>
						<ExpandMoreIcon />
					</IconButton>
				</CardActions>
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					<Tabs indicatorColor="primary" value={value} onChange={handleChange}>
						<Tab label="Items" />
						<Tab label="Finance History" />
						<Tab label="Event History" />
						<Tab label="Credit History" />
						<Tab label="Order Failures" />
					</Tabs>
					{value === 0 && (
						<CardContent>
							<LineItems
								customerId={props.customerId}
								order={order}
								orderId={order.orderId}
								orderNumber={order.orderNumber}
								lineItems={order.orderData.lineItems}
								updateParent={updateOrder}
								orderIsLegacy={order.isLegacy}
								isVCSAgent={isVCSAgent}
								isVCSOrder={isVCSOrder}
								orderRefInfo={
									financeHist
										? {
												totalCaptured: financeHist.totalCaptured,
												totalRemaining: financeHist.totalRemaining,
										  }
										: {
												totalCaptured: {
													amountInCents: order.orderData.orderTotal.centAmount,
													currency: order.orderData.orderTotal.currencyCode,
													fractionDigits: order.orderData.orderTotal.fractionDigits,
												},
												totalRemaining: {
													amountInCents: 0,
													currency: order.orderData.orderTotal.currencyCode,
													fractionDigits: order.orderData.orderTotal.fractionDigits,
												},
										  }
								}
								culture={defaultCulture}
								itemsNumber={order.orderData.lineItems.length}
								tenantId={order.orderData.tenantId}
								isAnonymousCustomer={order.orderData.orderPlacedAnonymously}
								events={eventHist}
								cartId={order.orderData.cartId}
								isFullyPaidWithMC={isFullyPaidWithMC()}
								paymentFullySuccessful={paymentFullySuccessful}
								orderCredits={orderCredits}
								orderCreationDate={props.order.orderData.orderCreationDate}
								orderIsReleasedToFulfillment={order.orderData.releasedToFulfillment}
								defaultCountryCode={defaultCountryCode}
								itemsKeyEvents={eventResponse ? eventResponse.itemEvents : []}
								isPayOnBillingAccountOrder={isPayOnBillingAccountOrder()}
								isCompanyCardOrder={isCompanyCardOrder()}
							/>
						</CardContent>
					)}
					{value === 1 && (
						<div style={{padding: 16}}>
							<FinanceHistory orderId={order.orderNumber} />
						</div>
					)}
					{value === 2 && (
						<EventHistoryV2 events={eventResponse} order={props.order} locale={defaultCulture} />
					)}
					{value === 3 && (
						// <CreditHistory
						// 	orderNumber={order.orderNumber}
						// 	lineItems={order.orderData.lineItems}
						// 	fractionDigits={order.orderData.orderTotal.fractionDigits}
						// 	orderCredits={orderCredits}
						// />
						<div style={{padding: 16}}>
							<CreditHistory orderId={order.orderNumber} />
						</div>
					)}
					{value === 4 && (
						<div style={{padding: 16}}>
							<OrderFailures orderId={order.orderNumber} />
						</div>
					)}
				</Collapse>
			</Card>
		</>
	)
}
